import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { SymptomRow } from "../../api/symptom.type";
import Card from "../shared/card";
import { SubmitButton } from "../shared/buttons/submit-button";
import Typography from "../shared/typography";
import { formatTimeStampToHumanReadableDate } from "../utls/time";

interface SymptomItemProps {
  symptom: SymptomRow;
}

const renderDate = (iso?: string | null) => {
  if (!iso || iso === "infinity") return null;
  try {
    return formatTimeStampToHumanReadableDate(iso);
  } catch {
    return null;
  }
};

export function SymptomItem({ symptom }: SymptomItemProps) {
  const navigate = useNavigate();
  const {
    symptom_type,
    symptom_value,
    symptom_unit,
    symptom_range,
    symptom_interpretation,
    symptom_comments,
    created_at,
    updated_at,
  } = symptom;

  const formattedValue = symptom_value
    ? symptom_unit
      ? `${symptom_value} ${symptom_unit}`
      : symptom_value
    : null;

  const askQuestionPrompt = `What does ${
    symptom_type || "this symptom"
  } with value ${formattedValue || "unknown"} mean?`;

  return (
    <Card variant="cream" elevation={1} className="w-full">
      <Card.Body className="space-y-6">
        <div>
          <Typography variant="subtitle1" className="text-brown-900">
            {symptom_type || "Symptom"}
          </Typography>
          {symptom_interpretation && (
            <Typography variant="body2" className="text-brown-700">
              Interpretation: {symptom_interpretation}
            </Typography>
          )}
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-2">
            {formattedValue && (
              <Typography variant="body1" className="text-brown-800">
                Value: {formattedValue}
              </Typography>
            )}
            {symptom_range && (
              <Typography variant="body1" className="text-brown-800">
                Range: {symptom_range}
              </Typography>
            )}
          </div>
          <div className="space-y-2">
            {renderDate(created_at) && (
              <Typography variant="body2" className="text-brown-600">
                Recorded: {renderDate(created_at)}
              </Typography>
            )}
            {renderDate(updated_at) && (
              <Typography variant="body2" className="text-brown-600">
                Updated: {renderDate(updated_at)}
              </Typography>
            )}
          </div>
        </div>

        {symptom_comments && (
          <div>
            <Typography variant="caption" className="uppercase text-brown-500">
              Comments
            </Typography>
            <Typography variant="body2" className="text-brown-800">
              {symptom_comments}
            </Typography>
          </div>
        )}
      </Card.Body>
      <Card.Footer className="flex justify-end">
        <SubmitButton
          size="sm"
          color="brown"
          icon={<QuestionMarkCircleIcon className="w-5 h-5 text-cream-500" />}
          onClick={() =>
            navigate(`/chat?question=${encodeURIComponent(askQuestionPrompt)}`)
          }
        >
          Ask Question
        </SubmitButton>
      </Card.Footer>
    </Card>
  );
}

export default SymptomItem;
